import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useCart, useAuth } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { shopApi } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CreditCard, Wallet } from "lucide-react";
import FreeShippingBar from "@/components/FreeShippingBar";
import { track, setUserData } from "@/lib/pixels";

export default function Checkout() {
  const { items } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [shipping, setShipping] = useState("standard");
  const [coupon, setCoupon] = useState("");
  const [autoApplied, setAutoApplied] = useState(false);
  const [provider, setProvider] = useState("stripe");
  const [useCredit, setUseCredit] = useState(false);
  const [address, setAddress] = useState({
    name: user?.name || "",
    email: user?.email || "",
    line1: user?.shipping_address?.line1 || "",
    city: user?.shipping_address?.city || "",
    state: user?.shipping_address?.state || "",
    zip: user?.shipping_address?.zip || "",
    country: user?.shipping_address?.country || "US",
  });
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [paymentsStatus, setPaymentsStatus] = useState({ stripe: "unknown", paypal: "unknown" });
  const storeCredit = Number(user?.store_credit || 0);

  // Guests + late auth resolves — refill address from user profile once it arrives
  useEffect(() => {
    if (!user) return;
    setAddress((cur) => ({
      ...cur,
      name: cur.name || user.name || "",
      email: cur.email || user.email || "",
      line1: cur.line1 || user.shipping_address?.line1 || "",
      city: cur.city || user.shipping_address?.city || "",
      state: cur.state || user.shipping_address?.state || "",
      zip: cur.zip || user.shipping_address?.zip || "",
      country: cur.country || user.shipping_address?.country || "US",
    }));
  }, [user?.user_id]);

  // Required shipping fields — checkout button stays disabled until all filled
  const REQUIRED = ["name", "email", "line1", "city", "state", "zip", "country"];
  const missing = REQUIRED.filter((k) => !(address[k] || "").trim());
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address.email || "");
  const shippingReady = missing.length === 0 && isEmailValid;

  // Report which payment providers are LIVE vs MOCK so the UI can show a warning
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/settings/payments`)
      .then((r) => {
        setPaymentsStatus(r.data || {});
        // If PayPal isn't real, force Stripe so shoppers can't accidentally simulate a payment.
        if ((r.data || {}).paypal === "mock") setProvider("stripe");
      })
      .catch(() => {});
  }, []);

  // Auto-apply pending coupon (from Exit-Intent modal) on first render
  useEffect(() => {
    try {
      const pending = localStorage.getItem("novalynx_pending_coupon");
      if (pending && !coupon) { setCoupon(pending); setAutoApplied(true); }
    } catch { /* ignore */ }
    // Fire InitiateCheckout only once when the shopper actually has items
    if (items.length > 0) {
      const subtotal = items.reduce((s, i) => s + i.quantity * i.unit_price, 0);
      track("InitiateCheckout", {
        value: subtotal,
        currency: "USD",
        items: items.map((i) => ({ id: i.product_id, quantity: i.quantity, price: i.unit_price })),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Feed guest checkout data into ad pixels for Advanced Matching (EMQ boost).
  useEffect(() => {
    setUserData({
      email: address.email,
      name: address.name,
      city: address.city,
      state: address.state,
      zip: address.zip,
      country: address.country,
    });
  }, [address.email, address.name, address.city, address.state, address.zip, address.country]);

  const clearCoupon = () => {
    setCoupon(""); setAutoApplied(false);
    try { localStorage.removeItem("novalynx_pending_coupon"); } catch { /* ignore */ }
  };

  useEffect(() => {
    if (items.length === 0) return;
    setLoading(true);
    shopApi.quote({ items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, variant: i.variant, bundle_id: i.bundle_id || null })),
      shipping_option: shipping, coupon_code: coupon || undefined, use_credit: useCredit, origin_url: window.location.origin })
      .then(setQuote).catch(() => toast.error("Failed to price cart")).finally(() => setLoading(false));
  }, [items, shipping, coupon, useCredit]);

  const pay = async () => {
    if (items.length === 0) { toast.error("Cart is empty"); return; }
    if (!shippingReady) {
      toast.error("Please complete your shipping details before checkout", {
        description: missing.length ? `Missing: ${missing.join(", ")}` : "Please enter a valid email address",
      });
      // Scroll to the first missing input for better UX
      const firstMissing = missing[0] || (!isEmailValid ? "email" : null);
      if (firstMissing) document.querySelector(`[data-testid="checkout-${firstMissing === "line1" ? "address" : firstMissing}"]`)?.focus();
      return;
    }
    setCheckingOut(true);
    try {
      const res = await shopApi.checkout({
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, variant: i.variant, bundle_id: i.bundle_id || null })),
        shipping_option: shipping, coupon_code: coupon || undefined,
        shipping_address: address, origin_url: window.location.origin, provider,
        use_credit: useCredit,
      });
      // Keep the cart AND the pending coupon intact — they only get cleared on OrderSuccess
      // when the payment actually completes. That way, cancelling PayPal/Stripe returns the
      // shopper to a fully-populated cart so they can retry or edit.
      window.location.href = res.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Checkout failed"); setCheckingOut(false); }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto py-24 text-center">
        <h1 className="font-display text-3xl font-bold mb-3">Your cart is empty</h1>
        <Button className="btn-primary" onClick={() => nav("/shop")}>Continue shopping</Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="font-display text-3xl md:text-4xl font-bold mb-8 tracking-tight">Checkout</h1>
      <div className="grid md:grid-cols-[1fr_400px] gap-10">
        <div className="space-y-8">
          <section>
            <h2 className="font-semibold text-lg mb-4">1. Contact & shipping</h2>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Full name</Label><Input value={address.name} onChange={(e) => setAddress({ ...address, name: e.target.value })} data-testid="checkout-name" /></div>
              <div><Label>Email</Label><Input type="email" value={address.email} onChange={(e) => setAddress({ ...address, email: e.target.value })} data-testid="checkout-email" /></div>
              <div className="col-span-2"><Label>Address</Label><Input value={address.line1} onChange={(e) => setAddress({ ...address, line1: e.target.value })} data-testid="checkout-address" /></div>
              <div><Label>City</Label><Input value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} data-testid="checkout-city" /></div>
              <div><Label>State</Label><Input value={address.state} onChange={(e) => setAddress({ ...address, state: e.target.value })} data-testid="checkout-state" /></div>
              <div><Label>ZIP</Label><Input value={address.zip} onChange={(e) => setAddress({ ...address, zip: e.target.value })} data-testid="checkout-zip" /></div>
              <div><Label>Country</Label><Input value={address.country} onChange={(e) => setAddress({ ...address, country: e.target.value })} data-testid="checkout-country" /></div>
            </div>
          </section>

          <section>
            <h2 className="font-semibold text-lg mb-4">2. Shipping method</h2>
            <RadioGroup value={shipping} onValueChange={setShipping} className="space-y-2">
              {[["standard","Standard — $4.99 · 5-7 days"],["express","Express — $12.99 · 2-3 days"],["overnight","Overnight — $24.99 · next day"]].map(([v, l]) => (
                <label key={v} className={`flex items-center gap-3 border rounded-xl p-3 cursor-pointer ${shipping === v ? "border-primary bg-primary/5" : "border-white/10"}`} data-testid={`ship-${v}`}>
                  <RadioGroupItem value={v} /><span className="text-sm">{l}</span>
                </label>
              ))}
            </RadioGroup>
          </section>

          <section>
            <h2 className="font-semibold text-lg mb-4">3. Payment</h2>
            {paymentsStatus.paypal === "mock" && (
              <div className="rounded-lg border border-primary/40 bg-primary/10 p-3 mb-3 text-xs" data-testid="paypal-demo-notice">
                <p className="font-bold text-primary uppercase tracking-widest">PayPal disabled on this preview</p>
                <p className="text-muted-foreground mt-1">
                  PayPal only works on your live site (<span className="text-primary">www.novalynxtech.com</span>). Use <strong>Card (Stripe)</strong> here to test.
                </p>
              </div>
            )}
            <RadioGroup value={provider} onValueChange={setProvider} className="grid grid-cols-2 gap-3">
              <label className={`flex items-center gap-3 border rounded-xl p-4 cursor-pointer ${provider === "stripe" ? "border-primary bg-primary/5" : "border-white/10"}`} data-testid="pay-stripe">
                <RadioGroupItem value="stripe" /><CreditCard className="h-5 w-5 text-primary" /><span className="text-sm">Card (Stripe)</span>
                {paymentsStatus.stripe === "test" && <span className="ml-auto text-[9px] uppercase font-bold text-primary bg-primary/20 px-1.5 py-0.5 rounded">TEST</span>}
              </label>
              <label
                className={`flex items-center gap-3 border rounded-xl p-4 relative ${paymentsStatus.paypal === "mock" ? "cursor-not-allowed opacity-50 border-white/5" : `cursor-pointer ${provider === "paypal" ? "border-primary bg-primary/5" : "border-white/10"}`}`}
                data-testid="pay-paypal"
                title={paymentsStatus.paypal === "mock" ? "PayPal is only available on your live site" : ""}
              >
                <RadioGroupItem value="paypal" disabled={paymentsStatus.paypal === "mock"} />
                <span className="text-sm font-bold text-blue-300">Pay</span><span className="text-sm font-bold text-blue-400">Pal</span>
                {paymentsStatus.paypal === "mock" && <span className="ml-auto text-[9px] uppercase font-bold text-muted-foreground bg-white/5 px-1.5 py-0.5 rounded">Unavailable</span>}
                {paymentsStatus.paypal === "sandbox" && <span className="ml-auto text-[9px] uppercase font-bold text-yellow-500 bg-yellow-500/10 px-1.5 py-0.5 rounded">SANDBOX</span>}
              </label>
            </RadioGroup>
          </section>
        </div>

        <aside className="rounded-2xl bg-card border border-white/5 p-6 h-fit sticky top-24">
          <h3 className="font-semibold mb-4">Order summary</h3>
          <div className="space-y-3 max-h-72 overflow-auto pr-2">
            {items.map((it, i) => (
              <div key={`${it.product_id}-${it.variant || i}`} className="flex gap-3 text-sm">
                <img src={it.image} className="h-14 w-14 rounded-lg object-cover" alt="" />
                <div className="flex-1"><p className="line-clamp-1">{it.title}</p><p className="text-xs text-muted-foreground">Qty {it.quantity}</p></div>
                <div>${(it.unit_price * it.quantity).toFixed(2)}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 border-t border-white/5 pt-4 space-y-2 text-sm">
            {quote && !quote.free_shipping_unlocked && quote.free_shipping_remaining > 0 && (
              <FreeShippingBar subtotal={quote.subtotal - (quote.bundle_discount || 0) - (quote.coupon_discount || 0) - (quote.tier_discount || 0)} threshold={quote.free_shipping_threshold} compact />
            )}
            {quote?.free_shipping_unlocked && (
              <FreeShippingBar subtotal={quote.free_shipping_threshold} threshold={quote.free_shipping_threshold} compact />
            )}
            {autoApplied && quote?.coupon_code && (
              <div className="flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2" data-testid="coupon-auto-applied">
                <span className="text-lg">🎁</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs uppercase tracking-widest text-primary font-bold">Coupon applied</p>
                  <p className="text-sm font-mono tracking-wider">{quote.coupon_code}</p>
                </div>
                <button onClick={clearCoupon} className="text-xs text-muted-foreground hover:text-white transition" data-testid="coupon-remove">Remove</button>
              </div>
            )}
            <div className="flex gap-2">
              <Input placeholder="Coupon code" value={coupon} onChange={(e) => { setCoupon(e.target.value.toUpperCase()); setAutoApplied(false); }} data-testid="coupon-input" />
            </div>
            <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>${quote?.subtotal.toFixed(2) || "—"}</span></div>
            {quote?.bundle_discount > 0 && <div className="flex justify-between text-primary" data-testid="checkout-bundle-discount"><span>Bundle discount</span><span>−${quote.bundle_discount.toFixed(2)}</span></div>}
            {quote?.tier_discount > 0 && <div className="flex justify-between text-primary"><span>{quote.tier_name} tier discount</span><span>−${quote.tier_discount.toFixed(2)}</span></div>}
            {quote?.coupon_discount > 0 && <div className="flex justify-between text-primary"><span>Coupon</span><span>−${quote.coupon_discount.toFixed(2)}</span></div>}
            <div className="flex justify-between text-muted-foreground"><span>Shipping</span><span>{quote?.shipping === 0 ? "FREE" : `$${quote?.shipping.toFixed(2) || "—"}`}</span></div>
            <div className="flex justify-between text-muted-foreground"><span>Tax</span><span>${quote?.tax.toFixed(2) || "—"}</span></div>
            {quote?.credit_applied > 0 && <div className="flex justify-between text-primary"><span>Store credit</span><span>−${quote.credit_applied.toFixed(2)}</span></div>}
            <div className="flex justify-between font-bold text-lg pt-2 border-t border-white/5"><span>Total</span><span data-testid="checkout-total">${quote?.total.toFixed(2) || "—"}</span></div>
          </div>
          {storeCredit > 0 && (
            <label className={`flex items-center justify-between mt-3 rounded-xl border px-3 py-2 cursor-pointer ${useCredit ? "border-primary bg-primary/10" : "border-white/10"}`} data-testid="use-credit-toggle">
              <div className="flex items-center gap-2">
                <Wallet className="h-4 w-4 text-primary" />
                <span className="text-sm">Use store credit (${storeCredit.toFixed(2)})</span>
              </div>
              <input type="checkbox" checked={useCredit} onChange={(e) => setUseCredit(e.target.checked)} className="accent-primary" />
            </label>
          )}
          <Button className="btn-primary w-full mt-4 h-12" onClick={pay} disabled={checkingOut || loading || !shippingReady} data-testid="checkout-pay-btn">
            {checkingOut
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting…</>
              : !shippingReady
                ? "Complete shipping details to continue"
                : `Pay $${quote?.total.toFixed(2) || "…"}`}
          </Button>
          {!shippingReady && (
            <p className="text-xs text-muted-foreground mt-2 text-center" data-testid="checkout-missing-note">
              {missing.length > 0
                ? `Please fill in: ${missing.map((k) => k === "line1" ? "address" : k).join(", ")}`
                : "Please enter a valid email address"}
            </p>
          )}
          <p className="text-[10px] text-muted-foreground text-center mt-3" data-testid="checkout-mode-label">Secure checkout · {provider === "paypal" ? "PayPal" : "Stripe"}</p>
        </aside>
      </div>
    </div>
  );
}
